import type React from 'react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Toggle } from '@/components/ui/toggle';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlignCenter,
  AlignHorizontalJustifyCenter,
  AlignLeft,
  AlignRight,
  AlignVerticalJustifyCenter,
  Columns3,
  Copy,
  Grid3X3,
  Maximize,
  PanelLeftClose,
  PanelLeftOpen,
  Redo2,
  Rows3,
  Trash2,
  Undo2,
  Wand2,
} from 'lucide-react';

export type AlignDirection = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom';
export type DistributeAxis = 'horizontal' | 'vertical';

interface FluxogramaToolbarProps {
  canUndo: boolean;
  canRedo: boolean;
  selectionCount: number;
  snapEnabled: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onDuplicateSelection: () => void;
  onDeleteSelection: () => void;
  onAlign: (dir: AlignDirection) => void;
  onDistribute: (axis: DistributeAxis) => void;
  onAutoLayout: () => void;
  onFitView: () => void;
  onToggleSnap: () => void;
  disabled?: boolean;
  /** Painel de formas (esquerda): com `onTogglePalette`, a barra ganha o botão de mostrar/ocultar. */
  paletteOpen?: boolean;
  onTogglePalette?: () => void;
  children?: React.ReactNode;
}

export function FluxogramaToolbar({
  canUndo,
  canRedo,
  selectionCount,
  snapEnabled,
  onUndo,
  onRedo,
  onDuplicateSelection,
  onDeleteSelection,
  onAlign,
  onDistribute,
  onAutoLayout,
  onFitView,
  onToggleSnap,
  disabled,
  paletteOpen,
  onTogglePalette,
  children,
}: FluxogramaToolbarProps) {
  const hasMultiple = selectionCount >= 2;
  const hasAny = selectionCount >= 1;
  const canDistribute = selectionCount >= 3;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center gap-1 px-2 py-1.5 border-b bg-background overflow-x-auto">
        {onTogglePalette && (
          <>
            <ToolbarButton
              icon={paletteOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
              label={paletteOpen ? 'Ocultar painel de formas' : 'Mostrar painel de formas'}
              onClick={onTogglePalette}
            />
            <Separator orientation="vertical" className="mx-1 h-6" />
          </>
        )}
        <ToolbarButton
          icon={<Undo2 className="h-4 w-4" />}
          label="Desfazer (Ctrl+Z)"
          onClick={onUndo}
          disabled={disabled || !canUndo}
        />
        <ToolbarButton
          icon={<Redo2 className="h-4 w-4" />}
          label="Refazer (Ctrl+Y)"
          onClick={onRedo}
          disabled={disabled || !canRedo}
        />

        <Separator orientation="vertical" className="mx-1 h-6" />

        <ToolbarButton
          icon={<Copy className="h-4 w-4" />}
          label="Duplicar seleção (Ctrl+D)"
          onClick={onDuplicateSelection}
          disabled={disabled || !hasAny}
        />
        <ToolbarButton
          icon={<Trash2 className="h-4 w-4" />}
          label="Excluir seleção (Del)"
          onClick={onDeleteSelection}
          disabled={disabled || !hasAny}
        />

        <Separator orientation="vertical" className="mx-1 h-6" />

        <ToolbarButton
          icon={<AlignLeft className="h-4 w-4" />}
          label={hasMultiple ? 'Alinhar à esquerda' : 'Alinhar texto à esquerda'}
          onClick={() => onAlign('left')}
          disabled={disabled || !hasAny}
        />
        <ToolbarButton
          icon={<AlignHorizontalJustifyCenter className="h-4 w-4" />}
          label={hasMultiple ? 'Centralizar horizontalmente' : 'Centralizar texto'}
          onClick={() => onAlign('center')}
          disabled={disabled || !hasAny}
        />
        <ToolbarButton
          icon={<AlignRight className="h-4 w-4" />}
          label={hasMultiple ? 'Alinhar à direita' : 'Alinhar texto à direita'}
          onClick={() => onAlign('right')}
          disabled={disabled || !hasAny}
        />
        <Separator orientation="vertical" className="mx-1 h-6" />
        <ToolbarButton
          icon={<AlignCenter className="h-4 w-4 rotate-90" />}
          label={hasMultiple ? 'Alinhar ao topo' : 'Alinhar texto ao topo'}
          onClick={() => onAlign('top')}
          disabled={disabled || !hasAny}
        />
        <ToolbarButton
          icon={<AlignVerticalJustifyCenter className="h-4 w-4" />}
          label={hasMultiple ? 'Centralizar verticalmente' : 'Centralizar texto verticalmente'}
          onClick={() => onAlign('middle')}
          disabled={disabled || !hasAny}
        />
        <ToolbarButton
          icon={<AlignCenter className="h-4 w-4 -rotate-90" />}
          label={hasMultiple ? 'Alinhar à base' : 'Alinhar texto à base'}
          onClick={() => onAlign('bottom')}
          disabled={disabled || !hasAny}
        />

        <Separator orientation="vertical" className="mx-1 h-6" />

        <ToolbarButton
          icon={<Columns3 className="h-4 w-4" />}
          label="Distribuir horizontalmente"
          onClick={() => onDistribute('horizontal')}
          disabled={disabled || !canDistribute}
        />
        <ToolbarButton
          icon={<Rows3 className="h-4 w-4" />}
          label="Distribuir verticalmente"
          onClick={() => onDistribute('vertical')}
          disabled={disabled || !canDistribute}
        />

        <Separator orientation="vertical" className="mx-1 h-6" />

        <ToolbarButton
          icon={<Wand2 className="h-4 w-4" />}
          label="Organizar automaticamente"
          onClick={onAutoLayout}
          disabled={disabled}
        />
        <ToolbarButton
          icon={<Maximize className="h-4 w-4" />}
          label="Enquadrar"
          onClick={onFitView}
        />

        <Separator orientation="vertical" className="mx-1 h-6" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Toggle
              size="sm"
              pressed={snapEnabled}
              onPressedChange={onToggleSnap}
              aria-label="Ajustar à grade"
              disabled={disabled}
            >
              <Grid3X3 className="h-4 w-4" />
            </Toggle>
          </TooltipTrigger>
          <TooltipContent>Ajustar à grade</TooltipContent>
        </Tooltip>

        {hasAny && (
          <div className="text-xs text-muted-foreground px-2 whitespace-nowrap">
            {selectionCount} {selectionCount === 1 ? 'item selecionado' : 'itens selecionados'}
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">{children}</div>
      </div>
    </TooltipProvider>
  );
}

interface ToolbarButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

function ToolbarButton({ icon, label, onClick, disabled }: ToolbarButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClick} disabled={disabled}>
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
